'use client';

import { useContext, useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'motion/react';
import { MapPin, XIcon } from 'lucide-react';

import { SectionLayout } from '@/components/shared/section-layout';
import SectionHeader from '@/components/shared/section-header';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { useIsCompactTouch } from '@/hooks/use-compact-touch';
import CardExpandButton from '@/components/shared/card-expand-button';
import { Carousel, CarouselContext } from '@/components/sections/recent-works/carousel';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { DesignRenderingAsset } from '@/sanity/lib/design-renderings';
import type { DesignRenderingsSectionContent } from '@/sanity/lib/sections';

/** Splits Sanity alt text like `Hotel • Lobby • Rendering • City, ST` into card fields. */
function parseStructuredRenderingAlt(alt: string): {
  projectName: string;
  area: string | null;
  location: string | null;
} | null {
  const parts = alt
    .split(/\s*[•·]\s*/u)
    .map((p) => p.trim())
    .filter(Boolean);

  const tokens = parts.filter((p) => p.toLowerCase() !== 'rendering');
  if (tokens.length < 2) return null;

  const projectName = tokens[0];
  if (!projectName) return null;

  if (tokens.length >= 3) {
    return {
      projectName,
      area: tokens[1] ?? null,
      location: tokens[tokens.length - 1] ?? null,
    };
  }

  const second = tokens[1] ?? '';
  const readsAsLocation =
    /^[^,]+,\s*[A-Za-z.]{2,}\s*$/u.test(second) || (second.includes(',') && second.length <= 120);

  if (readsAsLocation) {
    return { projectName, area: null, location: second };
  }

  return { projectName, area: second, location: null };
}

function renderingFallbackLabel(asset: DesignRenderingAsset) {
  const name = asset.originalFilename
    ?.replace(/^render--/i, '')
    .replace(/\.[^.]+$/u, '')
    .trim();
  if (name) return name.replace(/[-_]+/g, ' ');
  return 'Design rendering';
}

function getRenderingCardMeta(asset: DesignRenderingAsset) {
  const alt = asset.alt?.trim();
  if (alt) {
    const structured = parseStructuredRenderingAlt(alt);
    if (structured) {
      return {
        ...structured,
        imageAlt: alt,
        ariaLabel: [structured.projectName, structured.area, structured.location]
          .filter(Boolean)
          .join(', '),
      };
    }

    return {
      projectName: alt,
      area: null as string | null,
      location: null as string | null,
      imageAlt: alt,
      ariaLabel: alt,
    };
  }

  const fallback = renderingFallbackLabel(asset);
  return {
    projectName: fallback,
    area: null as string | null,
    location: null as string | null,
    imageAlt: fallback,
    ariaLabel: fallback,
  };
}

type RenderingCardMeta = ReturnType<typeof getRenderingCardMeta>;

function RenderingMetaOverlay({
  meta,
  mode,
}: {
  meta: RenderingCardMeta;
  mode: 'thumb' | 'lightbox';
}) {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-20 bg-[linear-gradient(180deg,rgba(0,0,0,0.24)_0%,rgba(0,0,0,0)_26%,rgba(0,0,0,0)_38%,rgba(0,0,0,0.82)_100%)]" />
      {meta.area ? (
        <Badge className="absolute top-4 left-4 z-40 w-fit rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[10px] font-bold tracking-widest text-white uppercase backdrop-blur-sm">
          {meta.area}
        </Badge>
      ) : null}
      {mode === 'thumb' ? <CardExpandButton /> : null}
      <div
        className={cn(
          'pointer-events-none absolute bottom-4 left-4 z-40 pr-10',
          mode === 'lightbox' && 'sm:pr-14',
        )}
      >
        <h2 className="line-clamp-2 text-base leading-none text-white sm:text-lg">
          {meta.projectName}
        </h2>
        {meta.location ? (
          <div className="mt-1 flex items-center gap-1 text-xs text-white/70">
            <MapPin className="size-3 shrink-0" />
            {meta.location}
          </div>
        ) : null}
      </div>
    </>
  );
}

function RenderingImageCard({
  asset,
  index,
  instanceKey,
  layout = false,
}: {
  asset: DesignRenderingAsset;
  index: number;
  instanceKey?: string;
  layout?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const isCompactTouch = useIsCompactTouch();
  const { onCardClose, suspendAutoplayForOverlay } = useContext(CarouselContext);

  useEffect(() => {
    if (!open) return;
    suspendAutoplayForOverlay(true);
    return () => suspendAutoplayForOverlay(false);
  }, [open, suspendAutoplayForOverlay]);

  const meta = getRenderingCardMeta(asset);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && !isCompactTouch) onCardClose(index);
  };

  const trigger = (
    <motion.button
      type="button"
      layoutId={layout ? `design-render-${asset._id}-${instanceKey ?? index}` : undefined}
      aria-label={`Open preview: ${meta.ariaLabel}`}
      className={cn(
        'group relative z-10 h-auto aspect-video w-full shrink-0 cursor-pointer touch-pan-y overflow-hidden rounded-xl bg-black text-left focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-white/40 border border-white/20 dark:border-white/10',
        'md:h-80 md:w-md xl:h-auto xl:w-full',
      )}
    >
      <Image
        src={asset.src}
        alt={meta.imageAlt}
        fill
        sizes="(max-width: 767px) calc(100vw - 2rem), (max-width: 1279px) 448px, 411px"
        className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02] group-hover:shadow-2xl"
      />
      <RenderingMetaOverlay meta={meta} mode="thumb" />
    </motion.button>
  );

  const lightboxImage = (
    <div
      className={cn(
        'relative flex w-full items-center justify-center overflow-hidden rounded-xl border border-white/20 bg-black dark:border-white/10',
        'max-md:rounded-none max-md:border-x-0',
      )}
    >
      <Image
        src={asset.src}
        alt={meta.imageAlt}
        width={2400}
        height={1600}
        className="h-auto w-full object-contain"
        sizes="(max-width: 768px) 96vw, min(1920px, 94vw)"
      />
      <RenderingMetaOverlay meta={meta} mode="lightbox" />
    </div>
  );

  if (isCompactTouch) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent
          showHandle={false}
          className="h-fit max-h-[88vh] overflow-hidden border-0 p-0 shadow-none ring-0 dark:bg-neutral-950 dark:border-t dark:border-neutral-700"
        >
          <DrawerTitle className="sr-only">{meta.ariaLabel}</DrawerTitle>
          <div className="max-h-[calc(88vh-2.25rem)] overflow-y-auto">{lightboxImage}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'grid-rows-[auto] gap-0 h-auto w-full overflow-visible border-none bg-transparent p-0!',
          'max-md:max-h-dvh max-md:max-w-[100vw] max-md:w-screen max-md:rounded-none',
          'md:max-w-5xl! md:rounded-xl',
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{meta.ariaLabel}</DialogTitle>
          <DialogDescription>Design rendering preview</DialogDescription>
        </DialogHeader>
        <DialogClose asChild>
          <button
            type="button"
            aria-label="Close"
            className="absolute top-3 right-3 z-60 flex size-8 sm:size-10 items-center justify-center rounded-full bg-[#948B7F] text-white hover:bg-[#BFB7A1] cursor-pointer transition-all duration-300 ease-in-out"
          >
            <XIcon className="size-4 sm:size-5" />
          </button>
        </DialogClose>
        {lightboxImage}
      </DialogContent>
    </Dialog>
  );
}

export default function DesignRenderings({
  assets,
  content,
}: {
  assets: DesignRenderingAsset[];
  content?: DesignRenderingsSectionContent | null;
}) {
  const { sectionSequenceRevealVariants, sectionSequenceBlockVariants } =
    useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();

  if (content?.show === false) return null;

  const { header } = content ?? {};
  const { primary, secondary } = sectionCtaPairFromContent(content?.showCTA, content?.ctaButtons);

  const cards = assets.filter((a) => a.src);
  const showCarouselBlock = content?.showCarousel !== false && cards.length > 0;
  const hasHeaderBody = Boolean(header?.tagline || header?.heading?.length || header?.subheading);

  if (!showCarouselBlock && !hasHeaderBody && !primary) return null;

  return (
    <SectionLayout
      id="design-renderings"
      className="relative scroll-mt-14 sm:scroll-mt-0 bg-white dark:bg-neutral-950 dark:border-t py-6 sm:py-12 xl:py-16 2xl:py-24"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div
        className="space-y-6 sm:space-y-12"
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
      >
        {hasHeaderBody ? (
          <SectionHeader
            sequenceChild
            content={{
              eyebrow: header?.tagline,
              description: header?.subheading,
            }}
            titleSlot={
              <h2 className="text-3xl sm:text-5xl leading-tight font-light tracking-tight text-foreground">
                <SanityInlineText
                  blocks={header?.heading}
                  emphasisClassName="italic font-normal text-[#948B7F] dark:text-[#FCFAD7]"
                />
              </h2>
            }
            eyebrowClassName="text-[#948B7F] dark:text-[#FCFAD7]"
            emphasisClassName="text-[#948B7F] dark:text-[#FCFAD7]"
            headingClassName="text-foreground"
            subheadingClassName="text-muted-foreground"
          />
        ) : null}
        {primary ? (
          <motion.div variants={sectionSequenceBlockVariants}>
            <SectionCtaPair
              primary={primary}
              secondary={secondary}
              trackingLocation="design_renderings"
            />
          </motion.div>
        ) : null}
        {showCarouselBlock ? (
          <motion.div variants={sectionSequenceBlockVariants}>
            <Carousel
              showControls={content?.showCarouselControls !== false}
              enableAutoplay={content?.enableAutoplay !== false}
              items={cards.map((asset, i) => (
                <RenderingImageCard key={asset._id} asset={asset} index={i} layout />
              ))}
            />
          </motion.div>
        ) : null}
      </motion.div>
    </SectionLayout>
  );
}
